/**
 * Standing up the family envelope.
 *
 * One screen, prefilled, because the alternative is a wizard and a wizard is
 * where this feature dies. Every field has a working default; a parent who
 * changes nothing and taps the button gets a functioning gauge with tiles they
 * can actually use. The tiles matter as much as the amount — an envelope with
 * no tiles fails the fifteen-second test on every subsequent use, so they are
 * set up here rather than left as an empty state to discover later.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BackLink } from '../ui/Nav.jsx';
import { useFamily } from '../lib/family.jsx';
import { createFuel } from '../lib/fuel.js';
import { centsFromDollars, formatCents } from '../domain/money.js';
import { CADENCES } from '../domain/periods.js';

/* The five from §14, as a starting point rather than a prescription. */
const STARTER_TILES = [
  { label: 'Ice cream', dollars: 6 },
  { label: 'Pizza', dollars: 25 },
  { label: 'Shakes', dollars: 12 },
  { label: 'Store run', dollars: 10 },
  { label: 'Takeaway', dollars: 30 },
];

const CADENCE_LABEL = { weekly: 'Every week', biweekly: 'Every two weeks', monthly: 'Every month' };

/**
 * The form, with no idea where its values go.
 *
 * Split from the route so it can be rendered without auth or a database —
 * `/preview/gauge` does exactly that. A screen nobody can look at until they
 * have a Supabase project and a signed-in guardian is a screen that gets
 * shipped unlooked-at.
 */
export function FuelSetupForm({ onSubmit, busy, error }) {
  const [dollars, setDollars] = useState('60');
  const [cadence, setCadence] = useState('weekly');
  const [capDollars, setCapDollars] = useState('120');
  const [tiles, setTiles] = useState(STARTER_TILES);
  const [goalName, setGoalName] = useState('');
  const [goalDollars, setGoalDollars] = useState('300');
  const amountCents = centsFromDollars(dollars);
  const perYear = CADENCES[cadence]?.perYear ?? 52;

  function editTile(i, patch) {
    setTiles((t) => t.map((tile, j) => (j === i ? { ...tile, ...patch } : tile)));
  }

  function submit(e) {
    e.preventDefault();
    onSubmit({
      amountCents,
      cadence,
      rolloverCapCents: centsFromDollars(capDollars),
      tiles: tiles
        .filter((t) => t.label.trim())
        .map((t) => ({ label: t.label.trim(), defaultAmountCents: centsFromDollars(t.dollars) })),
      goal: goalName.trim() ? { name: goalName.trim(), targetCents: centsFromDollars(goalDollars) } : null,
    });
  }

  return (
    <div className="stack">
      <form className="stack" onSubmit={submit}>
        <div className="card stack">
          <div className="row">
            <label style={{ flex: '1 1 8rem' }}>
              Amount in dollars
              <input
                type="number" inputMode="decimal" min="0" step="1" required
                value={dollars} onChange={(e) => setDollars(e.target.value)}
              />
            </label>
            <label style={{ flex: '1 1 10rem' }}>
              How often
              <select value={cadence} onChange={(e) => setCadence(e.target.value)}>
                {Object.keys(CADENCES).map((c) => (
                  <option key={c} value={c}>{CADENCE_LABEL[c]}</option>
                ))}
              </select>
            </label>
          </div>
          {/*
            The yearly figure is shown at set-up time on purpose. It is the same
            argument as the liability projection on bonus rules: a parent should
            see what a recurring commitment adds up to before agreeing to it,
            not discover it in a bank statement.
          */}
          <p className="small muted">
            That’s <strong>{formatCents(amountCents * perYear)}</strong> a year.
          </p>
        </div>

        <div className="card stack">
          <h2>Quick tiles</h2>
          <p className="small muted">
            One tap logs a spend. Set the amount you usually pay — it’s editable when it’s
            different, and being roughly right immediately beats being exactly right later.
          </p>
          {tiles.map((tile, i) => (
            <div className="row" key={i}>
              <label style={{ flex: '2 1 10rem' }}>
                <span className="visually-hidden">Name for tile {i + 1}</span>
                <input
                  value={tile.label} maxLength={40} placeholder="Ice cream"
                  onChange={(e) => editTile(i, { label: e.target.value })}
                />
              </label>
              <label style={{ flex: '1 1 6rem' }}>
                <span className="visually-hidden">Amount in dollars for {tile.label || `tile ${i + 1}`}</span>
                <input
                  type="number" inputMode="decimal" min="0" step="1" value={tile.dollars}
                  onChange={(e) => editTile(i, { dollars: e.target.value })}
                />
              </label>
            </div>
          ))}
          <button type="button" onClick={() => setTiles((t) => [...t, { label: '', dollars: 5 }])}>
            Add another tile
          </button>
        </div>

        <div className="card stack">
          <h2>What it rolls into</h2>
          <p className="small muted">
            Unspent fuel goes here. This is the part that makes skipping the ice cream feel
            like something rather than nothing — optional, and you can add it later.
          </p>
          <div className="row">
            <label style={{ flex: '2 1 10rem' }}>
              Goal
              <input
                value={goalName} maxLength={80} placeholder="Beach trip"
                onChange={(e) => setGoalName(e.target.value)}
              />
            </label>
            <label style={{ flex: '1 1 7rem' }}>
              Target in dollars
              <input
                type="number" inputMode="decimal" min="1" step="1" value={goalDollars}
                onChange={(e) => setGoalDollars(e.target.value)}
              />
            </label>
          </div>
          <label>
            Roll over at most, per period (dollars)
            <input
              type="number" inputMode="decimal" min="0" step="1" value={capDollars}
              onChange={(e) => setCapDollars(e.target.value)}
            />
          </label>
          <p className="small muted">
            A cap keeps the tank from quietly becoming a slush fund.
          </p>
        </div>

        {error && <p className="notice error" role="alert">{error}</p>}
        <button className="primary" type="submit" disabled={busy || amountCents <= 0}>
          {busy ? 'Setting up…' : 'Start the tank'}
        </button>
      </form>
    </div>
  );
}

export default function FuelSetup() {
  const { family } = useFamily();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function create(values) {
    setBusy(true); setError(null);
    try {
      await createFuel({ familyId: family.id, ...values });
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  return (
    <main className="app-shell stack">
      <div className="stack tight">
        <BackLink to="/">Home</BackLink>
        <h1>Fill the tank</h1>
      </div>
      <p className="muted">
        One pot for the fun stuff, topped up on a schedule. Everyone can see it, and what
        nobody spends goes toward something bigger.
      </p>
      <FuelSetupForm onSubmit={create} busy={busy} error={error} />
    </main>
  );
}
